import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DialogContentWithoutCloseButton } from "@/components/ui/dialog-no-close";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format, addDays, parse, addMinutes, isAfter, set, addHours, isBefore, isToday, startOfDay, endOfDay, parseISO } from 'date-fns';
import { CalendarIcon, Clock, MapPin, AlertCircle, CalendarOff, Check, ChevronsUpDown, PlusCircle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { triggerServiceWebhook } from "@/utils/webhookUtils";
import { PHONE_PREFIXES } from "@/constants/phone";
import { getPhoneMaxLength } from "@/constants/phoneValidation";
import { formatCurrency } from "@/utils/analyticsUtils";
import TimeSlotView from "@/components/service-requests/TimeSlotView";
import { ServiceRequest } from "@/components/service-requests/types";
import { useProfileFetch } from "@/hooks/useProfileFetch";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useTranslation } from "react-i18next";

interface BookedSlot {
  scheduled_at: string;
  service_id: string;
  duration: number;
  duration_unit: string;
  status: string;
}

interface BlockedDate {
  blocked_date: string;
  reason: string | null;
}

interface AvailabilitySetting {
  start_time: string;
  end_time: string;
  is_available: boolean;
}

interface Customer {
  name: string;
  phone?: string | null;
  email?: string | null;
  location?: string | null;
}

interface ServiceCheckoutDialogProps {
  isOpen: boolean;
  onClose: () => void;
  service: {
    id: string;
    name: string;
    price: number;
    discounted_price?: number | null;
    user_id: string;
    serviceMode?: string;
    travelFee?: string;
    currency?: string;
    instant_booking?: boolean;
    duration?: number;
    duration_unit?: string;
    service_area?: string;
    requires_scheduling?: boolean;
  };
  onSubmitSuccess?: () => void;
  contextType?: 'customer' | 'admin';
}

export function ServiceCheckoutDialog({ isOpen, onClose, service, onSubmitSuccess, contextType = 'customer' }: ServiceCheckoutDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phonePrefix, setPhonePrefix] = useState("+254");
  const [notes, setNotes] = useState("");
  const [location, setLocation] = useState("");
  const [date, setDate] = useState<Date | undefined>(addDays(new Date(), 1));
  const [time, setTime] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookedSlots, setBookedSlots] = useState<BookedSlot[]>([]);
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [phoneMaxLength, setPhoneMaxLength] = useState(10);
  const [conflictingSlots, setConflictingSlots] = useState<{[timeSlot: string]: string[]}>({});
  const [isDateBlocked, setIsDateBlocked] = useState<boolean>(false);
  const [blockedDateReason, setBlockedDateReason] = useState<string | null>(null);
  const [showTimeSlotCalendar, setShowTimeSlotCalendar] = useState<boolean>(false);
  const [selectedServicePreview, setSelectedServicePreview] = useState<{
    startTime: string;
    height: number;
    top: number;
  } | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [customerComboboxOpen, setCustomerComboboxOpen] = useState(false);
  const [providerCurrency, setProviderCurrency] = useState<string | null>(null);
  const { profile } = useProfileFetch();

  const schedulingRequired = service.requires_scheduling !== false;

  useEffect(() => {
    if (schedulingRequired) {
      fetchBlockedDates();
    }
    detectUserCountry();
    
    if (contextType === 'admin') {
      fetchCustomers();
    }
  }, [schedulingRequired, contextType]);

  useEffect(() => {
    if (schedulingRequired && date) {
      fetchBookedSlots(date);
      setTime('');
      setSelectedServicePreview(null);
    }
  }, [date, schedulingRequired]);

  useEffect(() => {
    // Log debug info when dialog opens
    if (isOpen && contextType === 'admin') {
      console.log('ServiceCheckoutDialog opened in admin context');
      console.log('Current customers data:', customers);
      
      // Re-fetch customers data when dialog opens
      if (customers.length === 0 && !isLoadingCustomers) {
        fetchCustomers();
      }
    }
  }, [isOpen, contextType, customers, isLoadingCustomers]);

  useEffect(() => {
    const fetchProviderCurrency = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('currency')
          .eq('id', service.user_id)
          .single();
          
        if (error) {
          console.error('Error fetching provider currency:', error);
          return;
        }
        
        if (data && data.currency) {
          console.log('Using provider currency:', data.currency);
          setProviderCurrency(data.currency);
        }
      } catch (error) {
        console.error('Error in fetchProviderCurrency:', error);
      }
    };
    
    fetchProviderCurrency();
  }, [service.user_id]);

  const detectUserCountry = async () => {
    try {
      const { data: providerData, error: providerError } = await supabase
        .from('profiles')
        .select('id, country')
        .eq('id', service.user_id)
        .single();
      
      if (!providerError && providerData?.country) {
        console.log("Using service provider's country:", providerData.country);
        const matchedPrefix = PHONE_PREFIXES.find(prefix => 
          prefix.label.includes(providerData.country)
        );
        
        if (matchedPrefix) {
          setPhonePrefix(matchedPrefix.value);
          return;
        }
      }

      const userLocale = navigator.language || 'en';
      const userCountry = userLocale.split('-')[1]?.toUpperCase();

      if (userCountry) {
        const matchedPrefix = PHONE_PREFIXES.find(prefix => 
          prefix.label.includes(userCountry)
        );
        
        if (matchedPrefix) {
          setPhonePrefix(matchedPrefix.value);
          return;
        }
      }

      const cachedCountry = localStorage.getItem('userCountry');
      if (cachedCountry) {
        const matchedPrefix = PHONE_PREFIXES.find(prefix => 
          prefix.label.includes(cachedCountry)
        );
        
        if (matchedPrefix) {
          setPhonePrefix(matchedPrefix.value);
          return;
        }
      }

      setPhonePrefix('+254');
    } catch (error) {
      console.error('Error in detectUserCountry:', error);
      setPhonePrefix('+254');
    }
  };

  const fetchBlockedDates = async () => {
    try {
      const { data, error } = await supabase
        .from('blocked_dates')
        .select('blocked_date, reason')
        .eq('user_id', service.user_id);

      if (error) throw error;
      setBlockedDates(data || []);
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
    }
  };

  const fetchBookedSlots = async (selectedDate: Date) => {
    try {
      setIsLoadingTimeSlots(true);
      setAvailableTimeSlots([]);
      setTime('');
      setConflictingSlots({});
      setIsDateBlocked(false);
      setBlockedDateReason(null);
      
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const blockedDateInfo = blockedDates.find(d => d.blocked_date === dateStr);
      
      if (blockedDateInfo) {
        setIsDateBlocked(true);
        setBlockedDateReason(blockedDateInfo.reason);
        setIsLoadingTimeSlots(false);
        return;
      }
      
      const startDay = startOfDay(selectedDate);
      const endDay = endOfDay(selectedDate);
      
      console.log(`Fetching booked slots between ${startDay.toISOString()} and ${endDay.toISOString()}`);
      
      const { data, error } = await supabase
        .from('service_requests')
        .select(`
          id,
          scheduled_at,
          service_id,
          status,
          services (
            id,
            name,
            duration,
            duration_unit
          )
        `)
        .eq('user_id', service.user_id)
        .in('status', ['accepted', 'completed', 'pending'])
        .gte('scheduled_at', startDay.toISOString())
        .lte('scheduled_at', endDay.toISOString());

      if (error) throw error;
      
      console.log('Fetched booked slots:', data);

      const bookedSlotsData = data?.map(item => ({
        scheduled_at: item.scheduled_at,
        service_id: item.service_id,
        status: item.status,
        duration: item.services?.duration || 60,
        duration_unit: item.services?.duration_unit || 'minutes'
      })) || [];

      setBookedSlots(bookedSlotsData);
      
      const dayOfWeek = selectedDate.getDay();
      const { data: availabilityData, error: availabilityError } = await supabase
        .from('availability_settings')
        .select('start_time, end_time, is_available')
        .eq('user_id', service.user_id)
        .eq('day_of_week', dayOfWeek);

      if (availabilityError) throw availabilityError;

      const availability = availabilityData?.[0];
      console.log('Availability settings for day', dayOfWeek, ':', availability);
      
      generateAvailableTimeSlots(selectedDate, availability);
    } catch (error) {
      console.error('Error fetching availability data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: t('servicePage.failedToLoadTimeSlots'),
      });
      setAvailableTimeSlots([]);
      setConflictingSlots({});
    } finally {
      setIsLoadingTimeSlots(false);
    }
  };

  const generateAvailableTimeSlots = (selectedDate: Date, availability: AvailabilitySetting | undefined) => {
    const slots: string[] = [];
    const conflicts: {[timeSlot: string]: string[]} = {};
    
    if (availability && availability.is_available) {
      const startTime = parse(availability.start_time, 'HH:mm:ss', new Date());
      const endTime = parse(availability.end_time, 'HH:mm:ss', new Date());
      
      console.log(`Provider available from ${format(startTime, 'HH:mm')} to ${format(endTime, 'HH:mm')}`);
      
      const serviceDuration = service?.duration || 60;
      const serviceDurationMinutes = service?.duration_unit === 'hours' 
        ? serviceDuration * 60 
        : serviceDuration;
      
      console.log(`Service duration: ${serviceDurationMinutes} minutes`);
      
      // Always use 30-minute intervals regardless of service duration
      const slotIntervalMinutes = 30;
      
      let currentSlot = new Date(startTime);
      while (isBefore(currentSlot, endTime)) {
        const timeStr = format(currentSlot, 'HH:mm');
        
        const serviceEndTime = addMinutes(currentSlot, serviceDurationMinutes);
        const fitsInWindow = isBefore(serviceEndTime, endTime) || serviceEndTime.getTime() === endTime.getTime();
        
        if (!fitsInWindow) {
          console.log(`Slot ${timeStr} doesn't fit in availability window (ends at ${format(serviceEndTime, 'HH:mm')})`);
          conflicts[timeStr] = [t('servicePage.serviceDurationExceedsWindow')];
          currentSlot = addMinutes(currentSlot, slotIntervalMinutes);
          continue;
        }
        
        const slotAvailability = isTimeSlotAvailable(timeStr, selectedDate);
        
        if (slotAvailability.available && !isTimeSlotInPast(timeStr, selectedDate)) {
          console.log(`Slot ${timeStr} is available`);
          slots.push(timeStr);
        } else if (!isTimeSlotInPast(timeStr, selectedDate)) {
          console.log(`Slot ${timeStr} has conflicts:`, slotAvailability.conflictsWith);
          conflicts[timeStr] = slotAvailability.conflictsWith || [];
        }
        
        currentSlot = addMinutes(currentSlot, slotIntervalMinutes);
      }
    } else {
      console.log('No specific availability settings, using default hours');
      
      const serviceDuration = service?.duration || 60;
      const serviceDurationMinutes = service?.duration_unit === 'hours' 
        ? serviceDuration * 60 
        : serviceDuration;
      
      // Always use 30-minute intervals regardless of service duration
      const slotIntervalMinutes = 30;
      const startHour = 8;
      const endHour = 20;
      
      const dayStart = set(selectedDate, { hours: startHour, minutes: 0 });
      const dayEnd = set(selectedDate, { hours: endHour, minutes: 0 });
      
      let currentSlot = new Date(dayStart);
      while (isBefore(currentSlot, dayEnd)) {
        const timeStr = format(currentSlot, 'HH:mm');
        const slotAvailability = isTimeSlotAvailable(timeStr, selectedDate);
        
        const serviceEndTime = addMinutes(currentSlot, serviceDurationMinutes);
        const fitsInDay = isBefore(serviceEndTime, dayEnd) || serviceEndTime.getTime() === dayEnd.getTime();
        
        if (!fitsInDay) {
          console.log(`Slot ${timeStr} doesn't fit in default availability window`);
          conflicts[timeStr] = ['Service duration exceeds default hours'];
        } else if (slotAvailability.available && !isTimeSlotInPast(timeStr, selectedDate)) {
          console.log(`Slot ${timeStr} is available`);
          slots.push(timeStr);
        } else if (!isTimeSlotInPast(timeStr, selectedDate)) {
          console.log(`Slot ${timeStr} has conflicts:`, slotAvailability.conflictsWith);
          conflicts[timeStr] = slotAvailability.conflictsWith || [];
        }
        
        currentSlot = addMinutes(currentSlot, slotIntervalMinutes);
      }
    }
    
    console.log('Available time slots:', slots);
    console.log('Conflicting time slots:', conflicts);
    
    setAvailableTimeSlots(slots);
    setConflictingSlots(conflicts);
    
    if (slots.length > 0) {
      if (time && slots.includes(time)) {
        setTime(time);
      } else {
        setTime('');
      }
    } else {
      setTime('');
    }
  };

  const isTimeSlotInPast = (timeStr: string, selectedDate: Date): boolean => {
    if (!isToday(selectedDate)) return false;
    
    const now = new Date();
    const [hours, minutes] = timeStr.split(':').map(Number);
    const slotTime = new Date(selectedDate);
    slotTime.setHours(hours, minutes, 0, 0);
    
    return isBefore(slotTime, now);
  };

  const isTimeSlotAvailable = (timeStr: string, selectedDate: Date): { available: boolean; conflictsWith?: string[] } => {
    if (!selectedDate) return { available: false };

    const [hours, minutes] = timeStr.split(':').map(Number);
    const selectedDateTime = new Date(selectedDate);
    selectedDateTime.setHours(hours, minutes, 0, 0);

    const serviceDuration = service?.duration || 60;
    const durationInMinutes = service?.duration_unit === 'hours' 
      ? serviceDuration * 60 
      : serviceDuration;
    
    const selectedEndTime = addMinutes(selectedDateTime, durationInMinutes);
    
    console.log(`Checking availability for ${timeStr} on ${format(selectedDate, 'yyyy-MM-dd')}`);
    console.log(`Service time would be from ${format(selectedDateTime, 'HH:mm')} to ${format(selectedEndTime, 'HH:mm')}`);
    console.log(`There are ${bookedSlots.length} booked slots to check against`);
    
    const conflictingAppointments = bookedSlots.filter(slot => {
      if (!slot.scheduled_at) return false;
      
      const bookedTime = parseISO(slot.scheduled_at);
      
      const bookedDurationMinutes = slot.duration_unit === 'hours' 
        ? slot.duration * 60 
        : slot.duration;
      
      const bookedEndTime = addMinutes(bookedTime, bookedDurationMinutes);
      
      const hasOverlap = (
        (selectedDateTime >= bookedTime && selectedDateTime < bookedEndTime) ||
        (selectedEndTime > bookedTime && selectedEndTime <= bookedEndTime) ||
        (selectedDateTime <= bookedTime && selectedEndTime >= bookedEndTime) ||
        (selectedDateTime.getTime() === bookedTime.getTime())
      );
      
      if (hasOverlap) {
        console.log(`Conflict found at ${timeStr}: `, {
          bookedTime: format(bookedTime, 'HH:mm'),
          bookedEndTime: format(bookedEndTime, 'HH:mm'),
          selectedTime: format(selectedDateTime, 'HH:mm'),
          selectedEndTime: format(selectedEndTime, 'HH:mm')
        });
      }
      
      return hasOverlap;
    });
    
    if (conflictingAppointments.length > 0) {
      return { 
        available: false, 
        conflictsWith: conflictingAppointments.map(appt => 
          format(parseISO(appt.scheduled_at), 'h:mm a')
        ) 
      };
    }
    
    return { available: true };
  };

  const handleTimeSelect = (selectedTime: string) => {
    console.log(`Time selected: ${selectedTime}`);
    setTime(selectedTime);
  };

  const handleSubmit = async () => {
    console.log('Submitting service request...');
    if (schedulingRequired && (!date || !time)) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please select a date and time.",
      });
      return;
    }

    if (!name.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter your name.",
      });
      return;
    }

    if (!phone.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter your phone number.",
      });
      return;
    }

    if (schedulingRequired && service.serviceMode === "client-location" && !location.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter your location.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      let scheduledAt: Date | null = null;

      if (schedulingRequired && date && time) {
        const [hours, minutes] = time.split(':').map(Number);
        scheduledAt = new Date(date);
        scheduledAt.setHours(hours, minutes, 0, 0);

        // Final check that the time slot is valid
        const { available, conflictsWith } = isTimeSlotAvailable(time, date);
        if (!available) {
          setIsSubmitting(false);
          toast({
            variant: "destructive",
            title: "Time Slot Not Available",
            description: `This time slot conflicts with: ${conflictsWith?.join(', ')}`,
          });
          return;
        }
      }

      const formattedPhone = `${phonePrefix}${phone.startsWith('0') ? phone.substring(1) : phone}`;

      const { data, error } = await supabase
        .from('service_requests')
        .insert([
          {
            user_id: service.user_id,
            service_id: service.id,
            customer_name: name.trim(),
            customer_email: email.trim() || null,
            customer_phone: formattedPhone.trim(),
            notes: notes.trim() || null,
            location: location.trim() || null,
            scheduled_at: scheduledAt ? scheduledAt.toISOString() : null,
            status: service.instant_booking ? 'accepted' : 'pending'
          }
        ])
        .select();

      if (error) throw error;

      if (data && data.length > 0) {
        try {
          // If the submission was successful, reset form and close the dialog
          resetForm();
          onClose();
          
          if (onSubmitSuccess) {
            onSubmitSuccess();
          }
          
          // Calculate the total price including travel fee if applicable
          const totalPrice = hasTravelFee 
            ? (service.discounted_price || service.price) + Number(service.travelFee)
            : (service.discounted_price || service.price);
          
          // Prepare notification data
          const notificationData = {
            userId: service.user_id,
            serviceName: service.name,
            servicePrice: `${(service.discounted_price || service.price).toString()}`,
            serviceStatus: service.instant_booking ? 'accepted' : 'pending',
            customerName: name.trim(),
            customerPhone: formattedPhone.trim(),
            customerEmail: email.trim() || "Not provided",
            appointmentDate: scheduledAt ? scheduledAt.toISOString() : "No date selected",
            formattedAppointmentDate: scheduledAt ? format(scheduledAt, 'PPpp') : "No date selected",
            specialRequests: notes.trim() || "None",
            location: location.trim() || "Not specified",
            requestId: data[0].id,
            autoApproved: service.instant_booking,
            totalPrice,
            currency: displayCurrency,
            includesTravelFee: hasTravelFee,
            providerCurrency: displayCurrency
          };

          console.log('Triggering service request webhook with data:', notificationData);
          await triggerServiceWebhook('service_request', notificationData, service.user_id);
          
          if (service.instant_booking) {
            await triggerServiceWebhook('service_accepted', notificationData, service.user_id);
          }
          
          console.log('Service request webhook triggered successfully');
        } catch (webhookError) {
          console.error('Failed to trigger service request webhook:', webhookError);
        }
      }

      toast({
        title: "Request Submitted",
        description: service.instant_booking
          ? "Your service request has been automatically approved! You'll receive confirmation shortly."
          : "Your service request has been submitted successfully.",
      });
    } catch (error) {
      console.error('Error submitting request:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to submit your request. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDateDisabled = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    const dateStr = format(date, 'yyyy-MM-dd');
    return checkDate < today || blockedDates.some(blockedDate => blockedDate.blocked_date === dateStr);
  };

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      console.log(`Date selected: ${format(selectedDate, 'yyyy-MM-dd')}`);
      setShowTimeSlotCalendar(true);
      setTime('');
    }
    setDate(selectedDate);
    setCalendarOpen(false);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    
    if (sanitizedValue.length <= phoneMaxLength) {
      setPhone(sanitizedValue);
    }
  };

  const businessName = useMemo(() => `${service.name} Service`, [service]);
  const canRequestService = !schedulingRequired || (schedulingRequired && date && time && !isDateBlocked);
  const servicePrice = service.discounted_price && service.discounted_price < service.price 
    ? service.discounted_price 
    : service.price;
  const displayPrice = servicePrice;
  
  // Use the provider's currency from fetched data first, then fallback to service.currency, then fallback to "KES"
  const displayCurrency = providerCurrency || service.currency || "KES";
  
  const isClientLocation = service.serviceMode === "client-location";
  const isProviderLocation = service.serviceMode === "my-location";
  const hasTravelFee = isClientLocation && service.travelFee && Number(service.travelFee) > 0;
  
  const showLocationSection = schedulingRequired && (isClientLocation || (isProviderLocation && service.service_area));

  const takenSlots: ServiceRequest[] = bookedSlots.map(slot => ({
    id: slot.scheduled_at,
    service_id: slot.service_id,
    user_id: service.user_id,
    customer_name: "",
    customer_email: null,
    customer_phone: null,
    status: slot.status as 'pending' | 'accepted' | 'rejected' | 'completed',
    notes: null,
    location: null,
    scheduled_at: slot.scheduled_at,
    created_at: new Date().toISOString(),
    paid: false,
    services: {
      id: slot.service_id,
      name: "",
      price: 0,
      duration: slot.duration,
      duration_unit: slot.duration_unit
    }
  }));

  const getLabelPrefix = () => contextType === 'admin' ? t('servicePage.customerName').replace(' Name', '') : t('servicePage.yourName').replace(' Name', '');

  const fetchCustomers = async () => {
    if (!service.user_id) return;
    
    setIsLoadingCustomers(true);
    
    try {
      console.log('Fetching customers for service provider:', service.user_id);
      
      const { data, error } = await supabase
        .from('service_requests')
        .select('customer_name, customer_phone, customer_email, location')
        .eq('user_id', service.user_id)
        .order('created_at', { ascending: false });
        
      if (error) {
        console.error('Supabase error fetching customers:', error);
        throw error;
      }
      
      console.log('Raw customer data from service requests:', data);
      
      if (!data || data.length === 0) {
        console.log('No customer data found');
        setCustomers([]);
        setIsLoadingCustomers(false);
        return;
      }
      
      // Create a unique list of customers by name
      const uniqueCustomersMap = new Map();
      
      data.forEach(item => {
        if (item.customer_name) {
          const existingCustomer = uniqueCustomersMap.get(item.customer_name);
          
          // If this customer doesn't exist yet, or if we found a more complete record
          if (!existingCustomer || 
              (!existingCustomer.phone && item.customer_phone) || 
              (!existingCustomer.email && item.customer_email) ||
              (!existingCustomer.location && item.location)) {
            
            uniqueCustomersMap.set(item.customer_name, {
              name: item.customer_name,
              phone: item.customer_phone || existingCustomer?.phone,
              email: item.customer_email || existingCustomer?.email,
              location: item.location || existingCustomer?.location
            });
          }
        }
      });
      
      const uniqueCustomers = Array.from(uniqueCustomersMap.values());
      
      console.log(`Successfully fetched and processed ${uniqueCustomers.length} unique customers:`, uniqueCustomers);
      setCustomers(uniqueCustomers || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  const handleCustomerSelect = (customerName: string) => {
    const selectedCustomer = customers.find(c => c.name === customerName);
    
    if (selectedCustomer) {
      console.log('Selected customer:', selectedCustomer);
      setName(selectedCustomer.name);
      
      if (selectedCustomer.phone) {
        console.log('Setting phone from:', selectedCustomer.phone);
        
        // Get the phone with prefix
        const phoneWithPrefix = selectedCustomer.phone;
        console.log('Phone with prefix:', phoneWithPrefix);
        
        // Find matching prefix from our available prefixes
        let foundPrefix = '+254'; // Default
        let phoneWithoutPrefix = phoneWithPrefix;
        
        // Try to match one of our known prefixes
        for (const prefixOption of PHONE_PREFIXES) {
          if (phoneWithPrefix.startsWith(prefixOption.value)) {
            foundPrefix = prefixOption.value;
            phoneWithoutPrefix = phoneWithPrefix.substring(prefixOption.value.length);
            console.log(`Found matching prefix: ${foundPrefix}, remaining phone: ${phoneWithoutPrefix}`);
            break;
          }
        }
        
        // If we couldn't match a known prefix but the phone starts with +, extract it
        if (foundPrefix === '+254' && phoneWithPrefix.startsWith('+') && !phoneWithPrefix.startsWith('+254')) {
          const customPrefixMatch = phoneWithPrefix.match(/^\+\d+/);
          if (customPrefixMatch && customPrefixMatch[0]) {
            foundPrefix = customPrefixMatch[0];
            phoneWithoutPrefix = phoneWithPrefix.substring(foundPrefix.length);
            console.log(`Extracted custom prefix: ${foundPrefix}, remaining phone: ${phoneWithoutPrefix}`);
          }
        }
        
        // Remove any leading zeros from the phone number
        if (phoneWithoutPrefix.startsWith('0')) {
          phoneWithoutPrefix = phoneWithoutPrefix.substring(1);
          console.log('Removed leading zero, phone now:', phoneWithoutPrefix);
        }
        
        // Set the values
        console.log('Setting phone prefix to:', foundPrefix);
        console.log('Setting phone number to:', phoneWithoutPrefix);
        
        setPhonePrefix(foundPrefix);
        setPhone(phoneWithoutPrefix);
      } else {
        // Clear phone if not available
        setPhone('');
        setPhonePrefix('+254');
      }
      
      if (selectedCustomer.email) {
        console.log('Setting email from:', selectedCustomer.email);
        setEmail(selectedCustomer.email);
      } else {
        // Clear email if not available
        setEmail('');
      }
      
      if (selectedCustomer.location) {
        console.log('Setting location from:', selectedCustomer.location);
        setLocation(selectedCustomer.location);
      } else {
        // Clear location if not available
        setLocation('');
      }
    }
  };

  const resetForm = () => {
    setName("");
    setEmail("");
    setPhone("");
    setNotes("");
    setLocation("");
    setDate(addDays(new Date(), 1));
    setTime("");
    setShowTimeSlotCalendar(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContentWithoutCloseButton className="max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="sticky top-0 z-10 bg-white pb-2 border-b">
          <DialogTitle>{t('servicePage.requestService')}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 my-4 overflow-y-auto px-1 flex-1">
          <div>
            <h3 className="font-semibold text-lg">{service.name}</h3>
            <p className="text-sm text-gray-600">
              {service.discounted_price !== undefined && service.discounted_price !== null ? (
                <>
                  <span className="line-through text-gray-400">
                    {formatCurrency(service.price, displayCurrency)}
                  </span>{' '}
                  <span className="text-green-600 font-medium">
                    {formatCurrency(service.discounted_price, displayCurrency)}
                  </span>
                </>
              ) : (
                <span>{formatCurrency(service.price, displayCurrency)}</span>
              )}
              {hasTravelFee && schedulingRequired && (
                <span className="ml-1">
                  + {formatCurrency(Number(service.travelFee), displayCurrency)} {t('servicePage.travelFee').replace(':', '')}
                </span>
              )}
            </p>
            
            {hasTravelFee && schedulingRequired && (
              <div className="mt-2 text-sm bg-gray-50 p-2 rounded">
                <div className="flex justify-between">
                  <span>{t('servicePage.serviceFee')}</span>
                  <span>{formatCurrency(displayPrice, displayCurrency)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('servicePage.travelFee')}</span>
                  <span>{formatCurrency(Number(service.travelFee), displayCurrency)}</span>
                </div>
                <div className="flex justify-between font-medium border-t border-gray-200 mt-1 pt-1">
                  <span>{t('servicePage.total')}</span>
                  <span>{formatCurrency(displayPrice + Number(service.travelFee), displayCurrency)}</span>
                </div>
              </div>
            )}
          </div>
          
          <div className="space-y-4">
            {contextType === 'admin' ? (
              <div className="flex flex-col">
                <label className="text-sm font-medium block mb-1">{t('servicePage.customerName')}*</label>
                <div className="relative">
                  <Popover open={customerComboboxOpen} onOpenChange={setCustomerComboboxOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={customerComboboxOpen}
                        disabled={isSubmitting}
                        className={cn(
                          "w-full justify-between",
                          !name && "text-muted-foreground"
                        )}
                      >
                        {name
                          ? name
                          : t('servicePage.selectOrEnterCustomer')}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                      <Command>
                        <CommandInput 
                          placeholder={t('servicePage.searchOrAddCustomer')} 
                          className="h-9"
                          value={name}
                          onValueChange={(value) => {
                            // Immediately update the name field to make typing responsive
                            setName(value);
                            
                            // If we have customers but none match exactly, clear related fields
                            if (customers.length > 0) {
                              const exactMatch = customers.find(
                                c => c.name.toLowerCase() === value.toLowerCase()
                              );
                              
                              if (!exactMatch && value !== name) {
                                console.log('No exact match for customer, clearing fields', value);
                                // We're typing a new customer name, clear fields
                                setEmail("");
                                setPhone("");
                                setLocation("");
                              }
                            }
                          }}
                        />
                        <CommandList>
                          <CommandEmpty>
                            {isLoadingCustomers ? (
                              <div className="flex items-center justify-center p-2">
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                {t('servicePage.loadingCustomers')}
                              </div>
                            ) : (
                              <div className="py-6 text-center text-sm">
                                {t('servicePage.noExistingCustomers')}
                              </div>
                            )}
                          </CommandEmpty>
                          <CommandGroup heading={t('servicePage.existingCustomers')}>
                            {customers
                              .filter(customer => 
                                customer.name.toLowerCase().includes(name.toLowerCase())
                              )
                              .slice(0, 10)
                              .map((customer) => (
                                <CommandItem
                                  key={customer.name}
                                  onSelect={() => {
                                    console.log('Customer selected from dropdown:', customer.name);
                                    handleCustomerSelect(customer.name);
                                    setCustomerComboboxOpen(false);
                                  }}
                                  className="flex items-center"
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      name === customer.name
                                        ? "opacity-100"
                                        : "opacity-0"
                                    )}
                                  />
                                  <div className="flex-1">
                                    <p className="font-medium">{customer.name}</p>
                                    <div className="flex flex-col text-xs text-muted-foreground">
                                      {customer.phone && (
                                        <span>{customer.phone}</span>
                                      )}
                                      {customer.email && (
                                        <span className="truncate max-w-[200px]">{customer.email}</span>
                                      )}
                                    </div>
                                  </div>
                                </CommandItem>
                              ))}
                          </CommandGroup>
                          {name && !customers.some(c => c.name === name) && (
                            <>
                              <CommandSeparator />
                              <CommandGroup heading={t('servicePage.createNew')}>
                                <CommandItem
                                  onSelect={() => {
                                    setName(name);
                                    setCustomerComboboxOpen(false);
                                  }}
                                >
                                  <PlusCircle className="mr-2 h-4 w-4" />
                                  {t('servicePage.create')} "{name}"
                                </CommandItem>
                              </CommandGroup>
                            </>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            ) : (
              <div>
                <label className="text-sm font-medium block mb-1">{getLabelPrefix()} {t('servicePage.name')}*</label>
                <Input 
                  placeholder={t('servicePage.fullName')} 
                  value={name} 
                  onChange={(e) => setName(e.target.value)} 
                />
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium block mb-1">{getLabelPrefix()} {t('servicePage.email')}</label>
              <Input 
                type="email" 
                placeholder={t('servicePage.emailAddress')} 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
              />
            </div>
            
            <div>
              <label className="text-sm font-medium block mb-1">{getLabelPrefix()} {t('servicePage.phone')}*</label>
              <div className="flex gap-2">
                <Select 
                  value={phonePrefix} 
                  onValueChange={setPhonePrefix}
                >
                  <SelectTrigger className="w-[130px] flex-shrink-0">
                    <SelectValue placeholder={t('servicePage.code')} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {PHONE_PREFIXES.map((prefix) => (
                      <SelectItem key={prefix.value} value={prefix.value}>
                        {prefix.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input 
                  placeholder={t('servicePage.phoneNumber')} 
                  value={phone} 
                  onChange={handlePhoneChange}
                  className="flex-1 placeholder:text-gray-300"
                  type="tel"
                  inputMode="numeric"
                  maxLength={phoneMaxLength}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t('servicePage.enterUpToDigits', { maxLength: phoneMaxLength })}
              </p>
            </div>
            
            {showLocationSection && (
              <>
                {isClientLocation && (
                  <div>
                    <label className="text-sm font-medium block mb-1">
                      {getLabelPrefix()} {t('servicePage.location')}<span className="text-red-500 ml-0.5">*</span>
                    </label>
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                      <Input 
                        placeholder={t('servicePage.whereServiceProvider')}
                        value={location} 
                        onChange={(e) => setLocation(e.target.value)} 
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {contextType === 'admin' ? 
                        t('servicePage.serviceProviderWillGo') : 
                        t('servicePage.serviceProviderWillCome')}
                    </p>
                  </div>
                )}
                
                {isProviderLocation && service.service_area && (
                  <div>
                    <label className="text-sm font-medium block mb-1">{t('servicePage.serviceLocation')}</label>
                    <div className="flex items-start gap-2 bg-gray-50 p-3 rounded">
                      <MapPin className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm">{service.service_area}</p>
                        <p className="text-xs text-gray-500 mt-1">{t('servicePage.youllNeedToGo')}</p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
            
            {!schedulingRequired && (
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-md text-sm text-amber-700 flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">{t('servicePage.noSchedulingRequired')}</p>
                  <p>{t('servicePage.noSchedulingDescription')}</p>
                </div>
              </div>
            )}
            
            {schedulingRequired && (
              <>
                <div>
                  <label className="text-sm font-medium block mb-1">{t('servicePage.preferredDate')}*</label>
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? (
                          format(date, "PPP")
                        ) : (
                          <span>{t('servicePage.pickADate')}</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={handleDateSelect}
                        initialFocus
                        disabled={isDateDisabled}
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                <div>
                  <label className="text-sm font-medium block mb-1">{t('servicePage.preferredTime')}*</label>
                  {isLoadingTimeSlots ? (
                    <div className="flex items-center gap-2 h-10 text-sm text-gray-500">
                      <img 
                        src="/lovable-uploads/f8714beb-a432-4da6-acab-3ef6f0a42367.png" 
                        alt={t('servicePage.loadingTimeSlots')} 
                        className="h-5 w-5 gebeya-spinner" 
                      />
                      {t('servicePage.loadingAvailableTimes')}
                    </div>
                  ) : isDateBlocked ? (
                    <div className="text-sm text-amber-600 border border-amber-200 bg-amber-50 p-3 rounded-md flex items-start gap-2">
                      <CalendarOff className="h-5 w-5 flex-shrink-0 mt-0.5 text-amber-600" />
                      <div>
                        <p className="font-medium">{t('servicePage.dateUnavailable')}</p>
                        {blockedDateReason ? (
                          <p>{t('servicePage.reason')} {blockedDateReason}</p>
                        ) : (
                          <p>{t('servicePage.providerMarkedUnavailable')}</p>
                        )}
                      </div>
                    </div>
                  ) : date ? (
                    <div className="border rounded-lg mb-4">
                      <div className="bg-white border-b px-3 py-2 flex justify-between items-center">
                        <h3 className="text-sm font-medium">{t('servicePage.availableTimesFor', { date: format(date, 'EEEE, MMMM d') })}</h3>
                        {time && (
                          <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            {t('servicePage.selected')} {format(parse(time, 'HH:mm', new Date()), 'h:mm a')}
                          </div>
                        )}
                      </div>
                      <div className="max-h-96 overflow-auto">
                        <TimeSlotView
                          date={date}
                          requests={takenSlots}
                          readOnly={true}
                          availableTimeSlots={availableTimeSlots}
                          conflictingSlots={conflictingSlots}
                          onTimeSlotSelect={handleTimeSelect}
                          highlightSelectedTime={time}
                          serviceDuration={service.duration}
                          serviceDurationUnit={service.duration_unit}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-amber-600 border border-amber-200 bg-amber-50 p-3 rounded-md flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5 text-amber-600" />
                      <div>
                        <p className="font-medium">{t('servicePage.selectDateFirst')}</p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
            
            <div>
              <label className="text-sm font-medium block mb-1">{t('servicePage.additionalNotes')}</label>
              <Textarea 
                placeholder={t('servicePage.specificRequests')} 
                value={notes} 
                onChange={(e) => setNotes(e.target.value)} 
                className="min-h-[100px]"
              />
            </div>
          </div>
        </div>
        
        <DialogFooter className="sticky bottom-0 z-10 bg-white pt-2 border-t mt-2">
          <div className="flex flex-col gap-2 w-full">
            <Button 
              onClick={handleSubmit}
              disabled={isSubmitting || 
                (schedulingRequired && (availableTimeSlots.length === 0 || !time || isDateBlocked)) ||
                (schedulingRequired && isClientLocation && !location.trim())}
              className="w-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90 text-white"
            >
              {isSubmitting ? t('servicePage.submitting') : t('servicePage.submitRequest')}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              className="w-full"
            >
              {t('servicePage.cancel')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContentWithoutCloseButton>
    </Dialog>
  );
}

export default ServiceCheckoutDialog;
